import React, { useState } from 'react';
import axios from 'axios';
import SocialShareButton from './SocialShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatePost = ({ user, onPostCreated }) => {
  const [postType, setPostType] = useState('recipe'); // recipe, photo, profile, offer
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: null,
    imagePreview: null,
    // Recipe specific
    ingredients: '',
    instructions: '',
    prepTime: '',
    cookTime: '',
    servings: '',
    // Offer specific
    offerPrice: '',
    originalPrice: '',
    validUntil: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [createdPost, setCreatedPost] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: reader.result,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const postData = {
        type: postType,
        title: formData.title,
        description: formData.description,
        image: formData.image,
        userId: user?.id,
        userName: user?.full_name || user?.username,
      };

      // Add type-specific data
      if (postType === 'recipe') {
        postData.recipe = {
          ingredients: formData.ingredients.split('\n').filter(i => i.trim()),
          instructions: formData.instructions.split('\n').filter(i => i.trim()),
          prepTime: formData.prepTime,
          cookTime: formData.cookTime,
          servings: formData.servings,
        };
      } else if (postType === 'offer') {
        postData.offer = {
          price: parseFloat(formData.offerPrice),
          originalPrice: parseFloat(formData.originalPrice),
          validUntil: formData.validUntil,
        };
      }

      const response = await axios.post(`${API}/posts/create`, postData);
      
      setCreatedPost(response.data);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        image: null,
        imagePreview: null,
        ingredients: '',
        instructions: '',
        prepTime: '',
        cookTime: '',
        servings: '',
        offerPrice: '',
        originalPrice: '',
        validUntil: '',
      });

      if (onPostCreated) {
        onPostCreated(response.data);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create post');
    }
    setSubmitting(false);
  };

  if (createdPost) {
    const shareUrl = `${window.location.origin}/post/${createdPost.id}`;
    
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Post Created!</h2>
          <p className="text-gray-600 mb-6">
            Your {postType} has been published successfully
          </p>
        </div>

        {formData.imagePreview && (
          <img 
            src={formData.imagePreview} 
            alt="Post" 
            className="w-full h-64 object-cover rounded-lg mb-4"
          />
        )}

        <div className="mb-6">
          <h3 className="font-semibold text-lg mb-2">{createdPost.title}</h3>
          <p className="text-gray-600">{createdPost.description}</p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <button
            onClick={() => setCreatedPost(null)}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Create Another Post
          </button>
          
          <SocialShareButton
            url={shareUrl}
            title={createdPost.title}
            description={createdPost.description}
            imageUrl={formData.imagePreview}
            hashtags={['Lambalia', 'HomeCooking', postType]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Create New Post</h2>

      {/* Post Type Selector */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <button
          onClick={() => setPostType('recipe')}
          className={`p-4 rounded-lg border-2 transition ${
            postType === 'recipe'
              ? 'border-purple-600 bg-purple-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="text-3xl mb-2">🍳</div>
          <div className="text-sm font-medium">Recipe</div>
        </button>

        <button
          onClick={() => setPostType('photo')}
          className={`p-4 rounded-lg border-2 transition ${
            postType === 'photo'
              ? 'border-purple-600 bg-purple-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="text-3xl mb-2">📸</div>
          <div className="text-sm font-medium">Food Photo</div>
        </button>

        <button
          onClick={() => setPostType('profile')}
          className={`p-4 rounded-lg border-2 transition ${
            postType === 'profile'
              ? 'border-purple-600 bg-purple-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="text-3xl mb-2">👨‍🍳</div>
          <div className="text-sm font-medium">Profile Update</div>
        </button>

        <button
          onClick={() => setPostType('offer')}
          className={`p-4 rounded-lg border-2 transition ${
            postType === 'offer'
              ? 'border-purple-600 bg-purple-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="text-3xl mb-2">💰</div>
          <div className="text-sm font-medium">Special Offer</div>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder={
              postType === 'recipe' ? 'e.g., Grandma\'s Famous Fried Rice' :
              postType === 'photo' ? 'e.g., Today\'s Special Creation' :
              postType === 'profile' ? 'e.g., Excited to Join Lambalia!' :
              'e.g., 20% Off This Weekend'
            }
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Tell us about your post..."
            required
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image {postType === 'photo' && '*'}
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={postType === 'photo'}
          />
          {formData.imagePreview && (
            <img 
              src={formData.imagePreview} 
              alt="Preview" 
              className="mt-3 w-full h-48 object-cover rounded-lg"
            />
          )}
        </div>

        {/* Recipe-specific fields */}
        {postType === 'recipe' && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prep Time
                </label>
                <input
                  type="text"
                  name="prepTime"
                  value={formData.prepTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="15 mins"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cook Time
                </label>
                <input
                  type="text"
                  name="cookTime"
                  value={formData.cookTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="30 mins"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Servings
                </label>
                <input
                  type="text"
                  name="servings"
                  value={formData.servings}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="4"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ingredients (one per line)
              </label>
              <textarea
                name="ingredients"
                value={formData.ingredients}
                onChange={handleChange}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="2 cups rice&#10;1 tbsp oil&#10;Salt to taste"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructions (one step per line)
              </label>
              <textarea
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Heat oil in pan&#10;Add rice and stir&#10;Season with salt"
              />
            </div>
          </>
        )}

        {/* Offer-specific fields */}
        {postType === 'offer' && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Price *
                </label>
                <input
                  type="number"
                  name="offerPrice"
                  value={formData.offerPrice}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="19.99"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Original Price
                </label>
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="29.99"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid Until
                </label>
                <input
                  type="date"
                  name="validUntil"
                  value={formData.validUntil}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </>
        )}

        {/* Submit Button */}
        <div className="flex items-center space-x-3 pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-medium disabled:opacity-50 transition"
          >
            {submitting ? 'Creating...' : 'Create Post'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePost;
